import React, { useState, useEffect } from 'react';
import { ChevronDown, ArrowRight, Smartphone, Zap } from 'lucide-react';
import Navbar from '../../layout/Navbar';
import Footer from '../../layout/Footer';
import { Link } from 'react-router-dom';

const MobileApps = () => {
    const [scrollY, setScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [showMore, setShowMore] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const observers = new Map();
        const elements = document.querySelectorAll('[data-animate]');

        elements.forEach((el, index) => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(prev => ({ ...prev, [index]: true }));
                    }
                },
                { threshold: 0.1 }
            );
            observer.observe(el);
            observers.set(index, observer);
        });

        return () => observers.forEach(observer => observer.disconnect());
    }, []);

    const stats = [
        { number: "7+", label: "Apps Developed" },
        // { number: "1M+", label: "Downloads" },
        { number: "3+", label: "Platforms Supported" },
        { number: "95%", label: "User Retention" }
    ];

    const features = [
        { name: 'Cross-Platform Development', desc: 'Build once, deploy on Android with React Native.' },
        { name: 'UI/UX Design', desc: 'Intuitive interfaces for seamless user experiences.' },
        { name: 'Performance Optimization', desc: 'Fast, responsive apps with minimal load times.' },
        { name: 'Push Notifications', desc: 'Engage users with real-time updates.' },
        { name: 'Offline Capabilities', desc: 'Functionality even without internet access.' },
        { name: 'App Store Optimization', desc: 'Strategies to boost visibility and downloads.' },
    ];

    const platforms = [
        // { name: "iOS", desc: "Native apps for Apple devices." },
        { name: "Android", desc: "Custom solutions for Google ecosystem." },
        { name: "React Native", desc: "Hybrid apps for efficiency." },
        { name: "Flutter", desc: "Beautiful UIs with fast performance." },
        // { name: "Swift", desc: "High-performance iOS development." },
        // { name: "Kotlin", desc: "Modern Android app building." },
    ];

    return (
        <div className="min-h-screen bg-white text-gray-800">
            <Navbar />

            {/* Hero Section */}
            <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto text-center">
                    <div
                        className="animate-fade-in-up"
                        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
                    >
                        <h1 className="text-5xl md:text-7xl font-bold mt-16 text-gray-800 leading-tight">
                            Mobile
                            <br />
                            <span className="text-green-500 inline-block animate-pulse">Apps</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxedc capitalize">
                            Developing <span className="text-green-500 font-semibold">innovative mobile applications</span> that connect businesses with users on-the-go.
                        </p>
                    </div>

                    {/* Scroll Indicator */}
                    <div className="animate-bounce mt-16">
                        <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-gray-900" data-animate>
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-8 text-center">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-white">
                                <div className="text-4xl md:text-5xl font-bold mb-2 text-green-400 animate-pulse">{stat.number}</div>
                                <div className="text-gray-300 text-lg">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Service Overview Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-200 ${isVisible[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Our Mobile <span className="text-green-500">Apps</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            We create <span className="text-green-500 font-semibold">robust, feature-rich mobile apps</span> using technologies like React Native and Flutter to meet diverse business requirements.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Approach */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <Smartphone className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Approach</h3>
                            <p className="text-gray-600 leading-relaxed">
                                User-focused design combined with <span className="text-green-500 font-semibold">agile development</span> for apps that perform flawlessly across devices.
                            </p>
                        </div>

                        {/* Benefits */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <Zap className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Key Benefits</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Boost engagement, streamline operations, and <span className="text-green-500 font-semibold">drive revenue</span> with our custom mobile solutions.
                            </p>
                        </div>

                        {/* Process */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <ArrowRight className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Process</h3>
                            <ul className="list-disc list-inside text-gray-600 space-y-2">
                                <li>Ideation & Wireframing</li>
                                <li>Development & Integration</li>
                                <li>Testing & QA</li>
                                <li>Launch & Maintenance</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-animate>
                <div className={`max-w-7xl mx-auto text-center transition-all duration-1000 delay-250 ${isVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                        Key <span className="text-green-500">Features</span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto capitalize mb-8">
                        Essential features that power our <span className="text-green-500 font-semibold">mobile app development</span>.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {features.map((feature, idx) => (
                            <div key={idx} className="bg-gray-50 p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border hover:border-green-300">
                                <Smartphone className="w-10 h-10 text-green-500 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{feature.name}</h3>
                                <p className="text-gray-600">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Platforms Section with Show More */}
            <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-300 ${isVisible[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Supported <span className="text-green-500">Platforms</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Versatile technologies for comprehensive mobile solutions.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 cursor-pointer">
                        {(showMore ? platforms : platforms.slice(0, 6)).map((platform, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-6 text-center hover:shadow-xl transform hover:scale-105 transition-all duration-300 border border-gray-100 group hover:border-green-200"
                            >
                                <h3 className="text-lg font-bold text-gray-600 mb-1 group-hover:text-green-600 transition-colors duration-300">{platform.name}</h3>
                                <p className="text-sm text-gray-600">{platform.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gray-900 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 delay-400 ${isVisible[4] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Launch Your <span className="text-green-400">Mobile App</span> Today
                    </h2>
                    <p className="text-xl text-gray-300 mb-8">
                        Transform your ideas into reality with OneHexa Tech's mobile expertise.
                    </p>
                    <Link to="/contact">
                        <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                            Contact Us
                        </button>
                    </Link>
                </div>
            </section>

            <Footer />

            <style jsx>{`
                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 1s ease-out;
                }
            `}</style>
        </div>
    );
};

export default MobileApps;